import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import { MaskedLines, Reveal } from "../components/Reveal";
import { JsonLd } from "../components/SEO";
import EditorialMarquee from "../components/EditorialMarquee";
import { serviceBySlug, SERVICES } from "../data/services";
import { api } from "../lib/api";
import {
  ArrowUpRight,
  Search,
  Target,
  Code2,
  PenTool,
  Share2,
  MessageCircle,
  Mail,
} from "lucide-react";

// Per-service line icon
const SERVICE_ICONS = {
  seo: Search,
  "paid-ads": Target,
  "web-and-app-development": Code2,
  "graphic-design": PenTool,
  "social-media": Share2,
  "whatsapp-marketing": MessageCircle,
  "email-marketing": Mail,
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = serviceBySlug(slug);

  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const heroImgY = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", "30%"]
  );

  const heroImgScale = useTransform(
    scrollYProgress,
    [0, 1],
    [1.02, 1.12]
  );

  const [projects, setProjects] = useState([]);
  const [activeRelatedSlug, setActiveRelatedSlug] = useState("");

  useEffect(() => {
    api
      .get("/portfolio")
      .then((r) => setProjects(r.data.slice(0, 4)))
      .catch(() => {});
  }, []);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-400 flex-col gap-4">
        <p>Service not found.</p>

        <button
          className="btn-primary"
          onClick={() => navigate("/services")}
        >
          Back to services →
        </button>
      </div>
    );
  }

  const otherServices = SERVICES.filter(
    (s) => s.slug !== service.slug
  );
  const activeRelated =
    otherServices.find((item) => item.slug === activeRelatedSlug) ||
    otherServices[0];
  const serviceSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: service.title,
        description: service.intro,
        url: `https://dorado9.com/services/${service.slug}`,
        image: service.hero_image,
        provider: { "@id": "https://dorado9.com/#organization" },
      },
      {
        "@type": "FAQPage",
        mainEntity: (service.faqs || []).map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ],
  };

  return (
    <>
      <JsonLd id="service" data={serviceSchema} />
      {/* HERO */}
      <section
        ref={heroRef}
        className="
          relative
          min-h-[75vh]
          w-full
          overflow-hidden
          bg-brand-bg
          pt-36
          sm:pt-40
          lg:pt-32
          pb-20
        "
      >
        <motion.div
          style={{
            y: heroImgY,
            scale: heroImgScale,
          }}
          className="absolute inset-0 opacity-45"
          data-testid="service-hero-image"
        >
          <img
            src={service.hero_image}
            alt={service.title}
            className="
              w-full
              h-full
              object-cover
              mix-blend-luminosity
            "
          />

          <div className="
            absolute
            inset-0
            bg-gradient-to-b
            from-brand-bg/40
            via-brand-bg/60
            to-brand-bg
          " />

          <div className="absolute inset-0 grain" />
        </motion.div>

        <div
          className="
            absolute
            top-1/2
            left-0
            -translate-y-1/2
            w-[45vw]
            h-[45vw]
            rounded-full
            pointer-events-none
          "
          style={{
            background:
              "radial-gradient(circle, rgba(102,76,221,0.20), transparent 60%)",
          }}
        />

        <div
          className="
            absolute
            bottom-0
            right-0
            w-[35vw]
            h-[35vw]
            rounded-full
            pointer-events-none
          "
          style={{
            background:
              "radial-gradient(circle, rgba(17,245,186,0.16), transparent 60%)",
          }}
        />

        <div className="
          relative
          z-10
          max-w-[1600px]
          mx-auto
          px-6
          lg:px-12
        ">
          <div className="flex items-end gap-6 mb-6">
            <span className="
              font-serif-accent
              text-brand-mint
              text-5xl
              lg:text-8xl
              leading-none
            ">
              {service.n}
            </span>

            <span className="eyebrow pb-2">
              {service.short}
            </span>
          </div>

          <h1
            className="
              font-display
              text-[13vw]
              sm:text-[10vw]
              lg:text-[8.5vw]
              leading-[0.86]
              tracking-tighter
              uppercase
              text-white
              max-w-6xl
            "
            data-testid="service-title"
          >
            <MaskedLines lines={[service.title]} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 1,
              delay: 0.7,
            }}
            className="
              mt-10
              max-w-2xl
              text-xl
              lg:text-2xl
              font-serif-accent
              text-brand-mint
            "
          >
            {service.tagline}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 1,
              delay: 0.9,
            }}
            className="
              mt-6
              max-w-2xl
              text-zinc-300
              text-base
              lg:text-lg
              leading-relaxed
            "
          >
            {service.intro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 1,
              delay: 1.1,
            }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/contact"
              className="btn-accent"
              data-testid="service-hero-cta-primary"
            >
              Get a proposal
              <ArrowUpRight size={16} />
            </Link>

            <Link
              to="/portfolio"
              className="
                inline-flex
                items-center
                gap-2
                rounded-full
                border
                border-brand-mint/40
                px-6
                py-3
                text-white
                transition-all
                duration-300
                hover:bg-brand-mint
                hover:text-brand-bg
                hover:border-brand-mint
              "
            >
              See related work
            </Link>
          </motion.div>
        </div>
      </section>

      <EditorialMarquee />

      {/* WHAT WE OFFER */}
      <section className="
        relative
        bg-brand-bg
        py-24
        lg:py-32
        px-6
        lg:px-12
      ">
        <div className="
          max-w-[1600px]
          mx-auto
          grid
          lg:grid-cols-12
          gap-16
          items-start
        ">
          <div className="
            lg:col-span-4
            lg:sticky
            lg:top-32
            self-start
          ">
            <Reveal>
              <p className="eyebrow mb-6">
                {service.sectionContent.offer.eyebrow}
              </p>

              <h2 className="
                font-display
                text-4xl
                lg:text-6xl
                leading-[0.95]
                tracking-tighter
                uppercase
              ">
                {service.sectionContent.offer.titleStart}{" "}

                <span className="
                  font-serif-accent
                  text-brand-mint
                  lowercase
                ">
                  {service.sectionContent.offer.accent}
                </span>{" "}

                {service.sectionContent.offer.titleEnd}
              </h2>

              <p className="
                mt-6
                text-zinc-400
                max-w-md
                leading-relaxed
              ">
                {service.sectionContent.offer.description}
              </p>
            </Reveal>
          </div>

          <div
            className="
              lg:col-span-8
              grid
              sm:grid-cols-2
              gap-5
              auto-rows-fr
            "
            data-testid="service-offer-grid"
          >
            {service.offer.map((o, i) => (
              <Reveal
                key={o.t}
                delay={i * 0.05}
                className="h-full"
              >
                <div
                  className="
                    spotlight-card
                    group
                    flex
                    h-full
                    min-h-[340px]
                    flex-col
                    border
                    border-[color:var(--border)]
                    p-6
                    lg:p-8
                    hover:border-brand-mint/50
                    transition-colors
                    duration-500
                  "
                  data-testid={`service-offer-${i}`}
                >
                  <div className="
                    flex
                    items-center
                    justify-between
                    mb-4
                  ">
                    <span className="
                      font-serif-accent
                      text-brand-mint
                      text-2xl
                    ">
                      0{i + 1}
                    </span>

                    <span className="
                      text-zinc-600
                      group-hover:text-brand-mint
                      transition-colors
                    ">
                      ✦
                    </span>
                  </div>

                  <h3 className="
                    font-display
                    text-xl
                    lg:text-2xl
                    uppercase
                    tracking-tight
                    min-h-[72px]
                  ">
                    {o.t}
                  </h3>

                  <p className="
                    mt-3
                    flex-1
                    text-zinc-400
                    text-sm
                    leading-relaxed
                  ">
                    {o.d}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="!hidden lg:grid lg:grid-cols-[0.95fr_1.05fr] lg:items-stretch lg:gap-16">
            <Link
              to={`/services/${activeRelated.slug}`}
              className="group relative hidden min-h-[560px] overflow-hidden rounded-[2rem] border border-brand-mint/25 bg-white/[0.02] lg:block"
            >
              <motion.img
                key={activeRelated.slug}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 0.32, scale: 1 }}
                transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                src={activeRelated.hero_image}
                alt=""
                className="absolute inset-0 h-full w-full object-cover mix-blend-luminosity"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/65 to-brand-bg/10" />
              <div className="relative flex h-full flex-col justify-end p-10">
                <p className="eyebrow mb-5">{activeRelated.n} · {activeRelated.short}</p>
                <h3 className="font-display max-w-md text-5xl uppercase leading-[0.9] tracking-tighter text-white transition-colors group-hover:text-brand-mint">
                  {activeRelated.title}
                </h3>
                <p className="mt-6 max-w-md text-zinc-300">{activeRelated.tagline}</p>
                <span className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-brand-mint">
                  Explore service <ArrowUpRight size={14} />
                </span>
              </div>
            </Link>

            <div className="hidden divide-y divide-white/10 border-y border-white/10 lg:block">
              {otherServices.map((item) => {
                const Icon = SERVICE_ICONS[item.slug] || ArrowUpRight;
                const isActive = item.slug === activeRelated.slug;
                return (
                  <Link
                    key={item.slug}
                    to={`/services/${item.slug}`}
                    onMouseEnter={() => setActiveRelatedSlug(item.slug)}
                    onFocus={() => setActiveRelatedSlug(item.slug)}
                    data-testid={`service-related-${item.slug}`}
                    className={`group flex items-center gap-6 px-2 py-7 transition-colors duration-300 ${isActive ? "text-brand-mint" : "text-white hover:text-brand-mint"}`}
                  >
                    <span className="font-serif-accent text-2xl text-brand-mint/70">{item.n}</span>
                    <span className={`flex h-11 w-11 items-center justify-center rounded-full border transition-colors ${isActive ? "border-brand-mint/50 bg-brand-mint/10" : "border-white/10"}`}>
                      <Icon size={19} strokeWidth={1.5} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-display text-2xl uppercase tracking-tight">{item.short}</span>
                      <span className="mt-1 block truncate text-sm text-zinc-500 group-hover:text-zinc-300">{item.tagline}</span>
                    </span>
                    <ArrowUpRight size={18} className="shrink-0 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
                  </Link>
                );
              })}
            </div>
          </div>

          <div data-lenis-prevent className="!hidden -mr-6 mt-12 gap-4 overflow-x-auto pb-5 pr-6 scrollbar-hide lg:hidden">
            {otherServices.map((item) => {
              const Icon = SERVICE_ICONS[item.slug] || ArrowUpRight;
              return (
                <Link
                  key={item.slug}
                  to={`/services/${item.slug}`}
                  data-testid={`service-related-${item.slug}`}
                  className="group flex w-[calc(100vw-5.5rem)] shrink-0 snap-start flex-col rounded-3xl border border-brand-mint/25 bg-white/[0.02] p-7"
                >
                  <div className="flex items-start justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full border border-brand-mint/30 bg-brand-mint/10 text-brand-mint"><Icon size={21} strokeWidth={1.5} /></span>
                    <span className="font-serif-accent text-3xl text-brand-mint/70">{item.n}</span>
                  </div>
                  <h3 className="mt-12 font-display text-3xl uppercase leading-[0.95] tracking-tight">{item.short}</h3>
                  <p className="mt-3 min-h-12 text-sm leading-relaxed text-zinc-400">{item.tagline}</p>
                  <span className="mt-9 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-brand-mint">Explore <ArrowUpRight size={14} /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* LATEST WORK */}
<section
  className="
    relative
    bg-brand-bg
    py-24
    lg:py-28
    px-6
    lg:px-12
    overflow-hidden
  "
>
  <div
    className="
      absolute
      -right-32
      top-1/2
      -translate-y-1/2
      w-[520px]
      h-[520px]
      rounded-full
      blur-[170px]
      opacity-[0.07]
      pointer-events-none
    "
    style={{
      background:
        "radial-gradient(circle, rgba(102,76,221,.85), transparent 68%)",
    }}
  />

  <div className="relative z-10 max-w-[1600px] mx-auto">

    {/* HEADER */}
    <div className="
      flex
      flex-col
      md:flex-row
      md:items-end
      justify-between
      gap-8
      mb-12
    ">
      <Reveal>
        <p className="eyebrow mb-5">
          Latest Work
        </p>

        <h2 className="
          font-display
          text-4xl
          lg:text-6xl
          tracking-tighter
          uppercase
          leading-[0.95]
          max-w-3xl
        ">
          A few things we've been{" "}
          <span className="font-serif-accent text-brand-mint lowercase">
            building lately.
          </span>
        </h2>
      </Reveal>

      <Reveal delay={0.15}>
        <Link
          to="/portfolio"
          className="
            btn-ghost
            text-sm
            inline-flex
            items-center
            gap-2
          "
        >
          View all work
          <ArrowUpRight size={16} />
        </Link>
      </Reveal>
    </div>

    {/* CAROUSEL */}
    <div
      className="
        flex
        gap-5
        overflow-x-auto
        snap-x
        snap-mandatory
        pb-5
        pr-12
        scrollbar-hide
      "
    >
      {projects.map((p, i) => (
        <Reveal
          key={p.id}
          delay={i * 0.06}
        >
          <Link
            to={`/portfolio/${p.slug}`}
            className="
              group
              block
              shrink-0
              snap-start
              w-[78vw]
              sm:w-[300px]
              lg:w-[320px]
            "
          >

            {/* IMAGE */}
            <div
              className="
                relative
                aspect-[4/3]
                overflow-hidden
                rounded-[22px]
                border
                border-white/[0.06]
                bg-white/[0.015]
              "
            >
              <img
                src={p.cover_image}
                alt={p.title}
                className="
                  w-full
                  h-full
                  object-cover
                  grayscale
                  transition-all
                  duration-[1000ms]
                  ease-out
                  group-hover:grayscale-0
                  group-hover:scale-105
                "
              />

              <div
                className="
                  absolute
                  inset-0
                  bg-gradient-to-t
                  from-black/45
                  via-transparent
                  to-transparent
                  opacity-70
                "
              />

              {/* ARROW */}
              <div
                className="
                  absolute
                  right-4
                  top-4
                  h-9
                  w-9
                  rounded-full
                  border
                  border-white/10
                  bg-black/35
                  backdrop-blur-md
                  flex
                  items-center
                  justify-center
                  opacity-0
                  translate-y-1
                  group-hover:opacity-100
                  group-hover:translate-y-0
                  transition-all
                  duration-300
                "
              >
                <ArrowUpRight
                  size={15}
                  className="text-white"
                />
              </div>
            </div>

            {/* DETAILS */}
            <div className="mt-4 flex items-start justify-between gap-4">

              <div className="min-w-0">
                <p className="
                  text-[10px]
                  uppercase
                  tracking-[0.22em]
                  text-brand-mint
                ">
                  {p.category}
                </p>

                <h3
                  className="
                    mt-1.5
                    font-display
                    text-xl
                    lg:text-2xl
                    uppercase
                    tracking-tight
                    text-white
                    group-hover:text-brand-mint
                    transition-colors
                    duration-300
                  "
                >
                  {p.title}
                </h3>
              </div>

              {p.year && (
                <span className="
                  shrink-0
                  font-serif-accent
                  text-brand-mint
                  text-base
                  opacity-70
                ">
                  {p.year}
                </span>
              )}

            </div>

          </Link>
        </Reveal>
      ))}
    </div>

  </div>
</section>

      {/* OTHER SERVICES */}
      <section
        className="
          relative
          bg-brand-bg
          py-24
          lg:py-36
          px-6
          lg:px-12
          overflow-hidden
        "
        data-testid="service-other-section"
      >
        <div
          className="
            absolute
            top-0
            left-1/2
            -translate-x-1/2
            w-[70vw]
            h-[40vw]
            rounded-full
            pointer-events-none
          "
          style={{
            background:
              "radial-gradient(circle, rgba(102,76,221,0.10), transparent 65%)",
          }}
        />

        <div className="
          max-w-[1500px]
          mx-auto
          relative
        ">
          <Reveal>
            <div className="
              text-center
              max-w-3xl
              mx-auto
              mb-20
              lg:mb-28
            ">
              <p className="eyebrow mb-6">
                Explore other pillars
              </p>

              <h2 className="
                font-display
                text-4xl
                lg:text-6xl
                uppercase
                tracking-tighter
                leading-[0.95]
              ">
                The stack is{" "}

                <span className="
                  font-serif-accent
                  text-brand-mint
                  lowercase
                ">
                  bigger than one channel.
                </span>
              </h2>

              <Link
                to="/services"
                className="
                  mt-8
                  inline-flex
                  items-center
                  gap-2
                  link-underline
                  text-sm
                  uppercase
                  tracking-[0.24em]
                  text-brand-mint
                "
                data-testid="service-other-all"
              >
                View all seven services
                <ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>

          <div className="hidden grid overflow-hidden rounded-[2rem] border border-brand-mint/20 bg-white/[0.015] lg:grid-cols-[0.95fr_1.05fr]">
            <Link
              to={`/services/${activeRelated.slug}`}
              className="group relative min-h-[360px] overflow-hidden border-b border-white/10 lg:min-h-[560px] lg:border-b-0 lg:border-r"
            >
              <motion.img
                key={activeRelated.slug}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 0.3, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                src={activeRelated.hero_image}
                alt=""
                className="absolute inset-0 h-full w-full object-cover mix-blend-luminosity"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/70 to-brand-bg/5" />
              <div className="relative flex h-full flex-col justify-end p-7 lg:p-10">
                <p className="eyebrow mb-4">{activeRelated.n} · {activeRelated.short}</p>
                <h3 className="font-display max-w-md text-4xl uppercase leading-[0.9] tracking-tighter transition-colors group-hover:text-brand-mint lg:text-5xl">{activeRelated.title}</h3>
                <p className="mt-5 max-w-md text-sm text-zinc-300 lg:text-base">{activeRelated.tagline}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-brand-mint">Explore service <ArrowUpRight size={14} /></span>
              </div>
            </Link>

            <div className="divide-y divide-white/10">
              {otherServices.map((item) => {
                const Icon = SERVICE_ICONS[item.slug] || ArrowUpRight;
                const isActive = item.slug === activeRelated.slug;
                return (
                  <Link
                    key={item.slug}
                    to={`/services/${item.slug}`}
                    onMouseEnter={() => setActiveRelatedSlug(item.slug)}
                    onFocus={() => setActiveRelatedSlug(item.slug)}
                    data-testid={`service-related-${item.slug}`}
                    className={`group flex items-center gap-4 px-6 py-5 transition-colors duration-300 lg:px-8 lg:py-7 ${isActive ? "bg-brand-mint/[0.07] text-brand-mint" : "text-white hover:bg-white/[0.03] hover:text-brand-mint"}`}
                  >
                    <span className="font-serif-accent text-xl text-brand-mint/70 lg:text-2xl">{item.n}</span>
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors ${isActive ? "border-brand-mint/50" : "border-white/10"}`}><Icon size={18} strokeWidth={1.5} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-display text-xl uppercase tracking-tight lg:text-2xl">{item.short}</span>
                      <span className="mt-1 block truncate text-sm text-zinc-500 group-hover:text-zinc-300">{item.tagline}</span>
                    </span>
                    <ArrowUpRight size={17} className="shrink-0 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="
            grid
            grid-cols-1
            sm:grid-cols-2
            lg:grid-cols-3
            gap-4
            lg:gap-5
            items-stretch
          ">
            {otherServices.map((o, i) => {
              const Icon =
                SERVICE_ICONS[o.slug] ||
                ArrowUpRight;

              return (
                <Reveal
                  key={o.slug}
                  delay={(i % 3) * 0.08}
                  className="h-full"
                >
                  <Link
                    to={`/services/${o.slug}`}
                    data-testid={`service-related-${o.slug}`}
                    style={{
                      backgroundImage: `linear-gradient(145deg, rgba(26,25,27,0.82), rgba(26,25,27,0.94)), url(${o.hero_image})`,
                    }}
                    className="
                      group
                      relative
                      flex
                      h-full
                      min-h-[300px]
                      flex-col
                      rounded-3xl
                      border
                      border-brand-mint/20
                      bg-cover
                      bg-center
                      p-8
                      lg:p-9
                      transition-all
                      duration-500
                      hover:-translate-y-1
                      hover:border-brand-mint/55
                      hover:shadow-[0_24px_70px_-35px_rgba(17,245,186,0.42)]
                    "
                  >
                    <span className="
                      ghost-number
                      absolute
                      top-5
                      right-6
                      text-5xl
                      lg:text-6xl
                      leading-none
                      select-none
                      pointer-events-none
                      opacity-85
                      group-hover:opacity-100
                      transition-opacity
                      duration-500
                    ">
                      {o.n}
                    </span>

                    <span className="
                      relative
                      inline-flex
                      h-16
                      w-16
                      items-center
                      justify-center
                      rounded-2xl
                      bg-brand-mint/[0.08]
                      border
                      border-brand-mint/25
                      group-hover:border-brand-mint/60
                      group-hover:-translate-y-1
                      transition-all
                      duration-500
                    ">
                      <Icon
                        size={26}
                        strokeWidth={1.5}
                        className="text-brand-mint"
                      />
                    </span>

                    <div className="
                      flex
                      flex-col
                      flex-1
                    ">
                      <h3 className="
                        font-display
                        text-2xl
                        lg:text-3xl
                        uppercase
                        tracking-tight
                        mt-7
                        text-white
                        group-hover:text-brand-mint
                        transition-colors
                        duration-500
                        min-h-0
                      ">
                        {o.short}
                      </h3>

                      <p className="
                        text-zinc-300/75
                        text-sm
                        mt-2
                        leading-relaxed
                        flex-1
                        min-h-0
                      ">
                        {o.tagline}
                      </p>
                    </div>

                    <div className="
                      mt-auto
                      self-start
                      flex
                      items-center
                      gap-3
                      pt-8
                    ">
                      <span className="
                        h-px
                        w-8
                        bg-brand-mint
                        origin-left
                        scale-x-100
                        group-hover:scale-x-100
                        transition-transform
                        duration-500
                      " />

                      <span className="
                        inline-flex
                        items-center
                        gap-1.5
                        text-xs
                        uppercase
                        tracking-[0.24em]
                        text-brand-mint/85
                        group-hover:text-brand-mint
                        transition-colors
                        duration-500
                      ">
                        Explore

                        <ArrowUpRight
                          size={13}
                          className="
                            group-hover:-translate-y-0.5
                            group-hover:translate-x-0.5
                            transition-transform
                            duration-500
                          "
                        />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="
        relative
        bg-brand-bg
        py-24
        lg:py-32
        px-6
        lg:px-12
        text-center
      ">
        <Reveal>
          <p className="eyebrow mb-6">
            Take the next step
          </p>

          <h2 className="
            font-display
            text-4xl
            lg:text-7xl
            leading-[0.9]
            tracking-tighter
            uppercase
            max-w-4xl
            mx-auto
          ">
            Let's ship{" "}

            <span className="
              font-serif-accent
              text-brand-mint
              lowercase
            ">
              {service.short.toLowerCase()}
            </span>{" "}

            for you.
          </h2>

          <Link
            to="/contact"
            className="
              btn-primary
              mt-10
              inline-flex
            "
            data-testid="service-final-cta"
          >
            Book a consultation
            <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </section>
    </>
  );
}
